import React, { useState } from 'react';
import { Input } from './ui/Input';
import { Button } from './ui/Button';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { useAccessCode } from '../hooks/useAccessCode';

interface AccessCodeInputProps {
    onValidCode: (code: string, discount: number) => void;
    onInvalidCode: () => void;
    disabled?: boolean;
    className?: string;
}

const AccessCodeInput: React.FC<AccessCodeInputProps> = ({
    onValidCode,
    onInvalidCode,
    disabled = false,
    className = ''
}) => {
    const [code, setCode] = useState('');
    const [validationState, setValidationState] = useState<'idle' | 'valid' | 'invalid'>('idle');
    const [appliedDiscount, setAppliedDiscount] = useState(0);
    const { validateCode: validateAccessCode, isValidating, error, clearError } = useAccessCode();

    const validateCode = async () => {
        console.log("=== Access Code Validation Started ===");
        console.log("Code entered:", code);
        
        try {
            const result = await validateAccessCode(code);
            console.log("Validation result:", result);
            
            if (result) {
                console.log("✅ Valid code - Discount:", result.discountAmount);
                setValidationState('valid');
                setAppliedDiscount(result.discountAmount);
                onValidCode(code.trim(), result.discountAmount);
            } else {
                console.log("❌ Invalid code");
                setValidationState('invalid');
                onInvalidCode();
            }
        } catch (error) {
            console.log("Validation error:", error);
            
            // If the cloud function doesn't exist yet or CORS error, provide a mock response for testing
            // Remove this block once the cloud function is deployed
            if (error && (error.toString().includes('NOT_FOUND') || error.toString().includes('CORS') || error.toString().includes('Failed to fetch'))) {
                console.log("Cloud function error - using mock validation");
                
                // Mock validation for testing - SAVE2NOW gives $2 discount
                const mockValid = code.toUpperCase() === 'SAVE2NOW';
                if (mockValid) {
                    console.log("✅ Mock validation passed - $2 discount");
                    setValidationState('valid');
                    setAppliedDiscount(2.00);
                    onValidCode(code.trim(), 2.00);
                } else {
                    console.log("❌ Mock validation failed");
                    setValidationState('invalid');
                    onInvalidCode();
                }
            } else {
                console.log("❌ Validation error:", error);
                setValidationState('invalid');
                onInvalidCode();
            }
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !isValidating && !disabled) {
            validateCode();
        }
    };

    const clearCode = () => {
        setCode('');
        setValidationState('idle');
        clearError();
        setAppliedDiscount(0);
        onInvalidCode();
    };

    return (
        <div className={`${className}`}>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
                Promotional Code
            </label>
            <div className="flex gap-3">
                <div className="relative flex-1">
                    <Input
                        type="text"
                        value={code}
                        onChange={(e) => {
                            setCode(e.target.value);
                            if (validationState !== 'idle') {
                                setValidationState('idle');
                                clearError();
                            }
                        }}
                        onKeyPress={handleKeyPress}
                        placeholder="Enter your code here"
                        disabled={disabled || isValidating}
                        className={`h-12 px-4 text-base font-medium ${
                            validationState === 'valid' 
                                ? 'border-green-400 focus:border-green-500 bg-green-50 pr-12' 
                                : validationState === 'invalid' 
                                ? 'border-red-400 focus:border-red-500 bg-red-50 pr-12' 
                                : 'border-gray-300 focus:border-blue-500 hover:border-gray-400 transition-colors'
                        }`}
                    />
                    {validationState === 'valid' && (
                        <CheckCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 h-6 w-6 text-green-500" />
                    )}
                    {validationState === 'invalid' && (
                        <XCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 h-6 w-6 text-red-500" />
                    )}
                </div>
                {validationState === 'valid' ? (
                    <Button
                        type="button"
                        variant="outline"
                        onClick={clearCode}
                        disabled={disabled}
                        className="h-12 px-6 border-gray-300 hover:bg-gray-50 transition-all duration-200"
                    >
                        <XCircle className="h-4 w-4 mr-2" />
                        Remove
                    </Button>
                ) : (
                    <Button
                        type="button"
                        onClick={validateCode}
                        disabled={disabled || isValidating || !code.trim()}
                        className="h-12 px-8 bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white font-semibold transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isValidating ? (
                            <Loader2 className="h-5 w-5 animate-spin" />
                        ) : (
                            'Apply Code'
                        )}
                    </Button>
                )}
            </div>
            
            {validationState === 'invalid' && error && (
                <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                    <XCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700 font-medium">
                        {error}
                    </p>
                </div>
            )}
        </div>
    );
};

export default AccessCodeInput;